import React from 'react';
import { Box, Typography, Paper, Chip, Checkbox, FormControlLabel, useTheme } from '@mui/material';
import {
    PlayArrow as VyhlaseniIcon,
    CheckCircle as JeduIcon,
    Cancel as NejeduIcon,
    DirectionsCar as DojezdIcon,
    LocalShipping as PosilaIcon,
    Campaign as ZpravaVeleniIcon,
    Forum as ZpravaIcon,
    Description as DokumentaceIcon,
    Stop as DeaktivaceIcon,
} from '@mui/icons-material';
import type { OsaPolozka, DruhOsy } from '../../api/JsdhNotifikaceService/generated/models/OsaPolozka';
import { useOznacDoZpravy } from '../../api/JsdhNotifikaceService/service/ZasahMutations';
import { formatTime } from '../../utils/utils';
import { formatujDobuReakce } from '../../utils/statistikyUtils';
import VyberKategorie from './VyberKategorie';

/** Druhy, u kterych ma smysl nabidnout oznaceni "do zpravy" (mirror BE `ZasahController#oznacDoZpravy`). */
const DRUHY_DO_ZPRAVY: Partial<Record<DruhOsy, 'zprava' | 'priloha'>> = {
    ZPRAVA: 'zprava',
    ZPRAVA_VELENI: 'zprava',
    DOKUMENTACE: 'priloha',
};

const IKONY: Record<DruhOsy, React.ReactNode> = {
    VYHLASENI: <VyhlaseniIcon fontSize="small" />,
    ODPOVED: <JeduIcon fontSize="small" />,
    DOJEZD: <DojezdIcon fontSize="small" />,
    POSILOVA_TECHNIKA: <PosilaIcon fontSize="small" />,
    ZPRAVA_VELENI: <ZpravaVeleniIcon fontSize="small" />,
    ZPRAVA: <ZpravaIcon fontSize="small" />,
    DOKUMENTACE: <DokumentaceIcon fontSize="small" />,
    DEAKTIVACE: <DeaktivaceIcon fontSize="small" />,
};

interface OsaRadekProps {
    poplachId: number;
    polozka: OsaPolozka;
    /** Poradi mezi nejrychlejsimi (1-3), 0 = mimo medailove pozice. */
    poradiNejrychlejsi?: number;
    isAdmin?: boolean;
    /** `zamek === 'UZAMCENO'` na rodicovske ose - oznacovani je pak jen ke cteni. */
    zamceno?: boolean;
    isLast?: boolean;
}

/**
 * Jeden radek casove osy zasahu (HO-151 Task 1). Vykresluje `popis` tak, jak
 * prisel ze serveru - u ZPRAVA/ZPRAVA_VELENI je to skutecny text zpravy,
 * u ostatnich druhu pevna sablona (viz task-1-brief). U ODPOVED se text
 * NESKLADA z popis, ale ze strukturovaneho pole `odpoved` - formulace
 * "odpovedel JEDU/NEJEDU" popisuje umysl, ne dochazku ("byl na miste" apod.
 * jsou zakazane formulace).
 */
const OsaRadek: React.FC<OsaRadekProps> = ({ poplachId, polozka, poradiNejrychlejsi = 0, isAdmin = true, zamceno = false, isLast = false }) => {
    const theme = useTheme();
    const oznacDoZpravy = useOznacDoZpravy(poplachId);

    const { druh, cas, popis, entitaId, autor, smazano, doZpravy, kategorie } = polozka;
    const testId = `osa-polozka-${druh.toLowerCase()}-${entitaId}`;
    const jeNejrychlejsi = poradiNejrychlejsi > 0 && poradiNejrychlejsi <= 3;

    const typDoZpravy = DRUHY_DO_ZPRAVY[druh];
    const nabidniDoZpravy = !!typDoZpravy && !smazano && entitaId !== null;
    const ovladaniZakazano = !isAdmin || zamceno;

    const handleDoZpravyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!typDoZpravy || entitaId === null) return;
        oznacDoZpravy.mutate({
            typ: typDoZpravy,
            entitaId,
            doZpravy: event.target.checked,
            // Zaskrtnuti samo kategorii nenastavuje (ta se vybira az pres
            // VyberKategorie po zaskrtnuti); odskrtnuti ji musi shodit na
            // null, jinak CHECK ck_kz_kategorie/ck_p_kategorie (vyzaduje
            // do_zpravy = FALSE AND kategorie IS NULL) shodi zapis 500-kou.
            kategorie: null,
        });
    };

    const handleKategorieChange = (novaKategorie: string | null) => {
        if (!typDoZpravy || entitaId === null) return;
        oznacDoZpravy.mutate({
            typ: typDoZpravy,
            entitaId,
            doZpravy: true,
            kategorie: novaKategorie,
        });
    };

    const barvyIkony: Record<DruhOsy, string> = {
        VYHLASENI: theme.palette.error.main,
        ODPOVED: polozka.odpoved === 'JEDU' ? theme.palette.success.main : theme.palette.error.main,
        DOJEZD: theme.palette.primary.main,
        POSILOVA_TECHNIKA: theme.palette.warning.main,
        ZPRAVA_VELENI: theme.palette.secondary.main,
        ZPRAVA: theme.palette.info.main,
        DOKUMENTACE: theme.palette.info.dark,
        DEAKTIVACE: theme.palette.grey[600],
    };
    const iconBg = barvyIkony[druh];

    const icon = druh === 'ODPOVED' && polozka.odpoved !== 'JEDU' ? <NejeduIcon fontSize="small" /> : IKONY[druh];

    return (
        <Box data-testid={testId} sx={{ display: 'flex', position: 'relative' }}>
            {!isLast && (
                <Box
                    sx={{
                        position: 'absolute',
                        left: 15,
                        top: 32,
                        bottom: -16,
                        width: 2,
                        bgcolor: 'divider',
                    }}
                />
            )}

            <Box
                sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: iconBg,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    flexShrink: 0,
                    zIndex: 1,
                }}
            >
                {icon}
            </Box>

            <Paper variant="outlined" sx={{ ml: 2, mb: 2, p: 1.5, flex: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
                    <Box sx={{ minWidth: 0 }}>
                        {autor && (
                            <Typography variant="subtitle2" fontWeight="bold">
                                {autor}
                            </Typography>
                        )}
                        {smazano ? (
                            <Typography variant="body2" color="text.disabled" fontStyle="italic">
                                Zpráva byla smazána.
                            </Typography>
                        ) : druh === 'ODPOVED' ? (
                            <Typography variant="body2">
                                {(autor || 'Člen jednotky')} odpověděl {polozka.odpoved}
                            </Typography>
                        ) : (
                            <Typography variant="body2">
                                {popis}
                            </Typography>
                        )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                        {formatTime(cas)}
                    </Typography>
                </Box>

                {druh === 'ODPOVED' && !smazano && (
                    <Box display="flex" alignItems="center" gap={1} mt={1} flexWrap="wrap">
                        <Chip
                            label={polozka.odpoved}
                            color={polozka.odpoved === 'JEDU' ? 'success' : 'error'}
                            size="small"
                        />
                        <Chip
                            label={`Reakce: ${formatujDobuReakce(polozka.dobaReakceSekundy)}`}
                            variant="outlined"
                            size="small"
                        />
                        {jeNejrychlejsi && (
                            <Chip
                                label={
                                    poradiNejrychlejsi === 1 ? '1. nejrychlejší'
                                        : poradiNejrychlejsi === 2 ? '2. nejrychlejší'
                                            : '3. nejrychlejší'
                                }
                                sx={{
                                    bgcolor: poradiNejrychlejsi === 1 ? '#FFD700' : poradiNejrychlejsi === 2 ? '#C0C0C0' : '#CD7F32',
                                    color: poradiNejrychlejsi === 1 ? '#000' : '#fff',
                                }}
                                size="small"
                            />
                        )}
                    </Box>
                )}

                {nabidniDoZpravy && (
                    <Box display="flex" alignItems="center" gap={1} mt={0.5} flexWrap="wrap">
                        <FormControlLabel
                            sx={{ ml: 0 }}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={doZpravy}
                                    disabled={ovladaniZakazano}
                                    onChange={handleDoZpravyChange}
                                />
                            }
                            label="Do zprávy"
                        />
                        {doZpravy && (
                            <VyberKategorie
                                hodnota={kategorie}
                                onZmena={handleKategorieChange}
                                disabled={ovladaniZakazano}
                            />
                        )}
                    </Box>
                )}
            </Paper>
        </Box>
    );
};

export default OsaRadek;
